import { TicketAdditionalInfo, TicketModel } from '@/v2/models/ticketModel';

export const RESERVED_ADDITIONAL_INFO_TYPES = {
  /** Rewords the section/row/seat captions on the ticket band, leaving the values themselves alone. */
  locatorCaptions: ['section', 'row', 'seat'],
} as const;

export type ReservedAdditionalInfoType = keyof typeof RESERVED_ADDITIONAL_INFO_TYPES;
export type ReservedKey<T extends ReservedAdditionalInfoType> = (typeof RESERVED_ADDITIONAL_INFO_TYPES)[T][number];

export function normalizeAdditionalInfoField(field: string | undefined | null): string {
  return field?.trim().toLowerCase() ?? '';
}

/**
 * The values a host has set for the keys of one reserved type.
 *
 * The registry is the single source of truth for which keys a type reads, so the caller names only
 * the type. Casing and padding are resolved on both sides, blank values are dropped so the renderer's
 * own default stands, and when a key arrives twice the lowest `index` wins. The ticket is left
 * untouched.
 */
export function getAdditionalInfoValues<T extends ReservedAdditionalInfoType>(
  ticket: TicketModel,
  type: T,
): Partial<Record<ReservedKey<T>, string>> {
  const wantedType = normalizeAdditionalInfoField(type);
  const declaredKeys: readonly ReservedKey<T>[] = RESERVED_ADDITIONAL_INFO_TYPES[type];
  const declaredKeyByNormalized = new Map<string, ReservedKey<T>>(
    declaredKeys.map((key) => [normalizeAdditionalInfoField(key), key]),
  );

  return (ticket?.additionalInfos ?? [])
    .filter((info: TicketAdditionalInfo) => normalizeAdditionalInfoField(info?.type) === wantedType)
    .sort((a: TicketAdditionalInfo, b: TicketAdditionalInfo) => a.index - b.index)
    .reduce<Partial<Record<ReservedKey<T>, string>>>((values, info) => {
      const key = declaredKeyByNormalized.get(normalizeAdditionalInfoField(info.key));
      const value = info.value?.trim();
      if (key && value && !(key in values)) {
        values[key] = value;
      }
      return values;
    }, {});
}
